import { IHistoryLogDepricated } from "@mctypes/global.types";
import { Badge, Flex } from "@mantine/core";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useState } from "react";
import { AG_GRID_LOCALE_NOR } from "../agGridLocale";
import { formatDate, formatShortDate, isDateValidFrontend } from "../app/routes/admin/selectors";

const isEmpty = (v: unknown) => v == null || (typeof v === "string" && v.trim() === "");
type HistoryLogColumn = {
  field?: string;
  headerName?: string;
};

const HistoryLogsDepricated: React.FC<{
  itemId: string;
  modelColumns: HistoryLogColumn[];
  projectId: string;
  getLogs: (projectId: string, itemId: string) => Promise<IHistoryLogDepricated[]>;
}> = ({ itemId, modelColumns, projectId, getLogs }) => {
  const [logs, setLogs] = useState<IHistoryLogDepricated[] | null>(null);

  useEffect(() => {
    async function fetchData() {
      const _logs = await getLogs(projectId, itemId);
      _logs.forEach((log) => {
        const column = modelColumns.find((column) => column.field === log.column);
        log.columnDisplayName = column?.headerName ?? log.column;
      });
      setLogs(_logs);
    }
    fetchData();
  }, []);

  const formatValue = (val: any) => {
    if (isEmpty(val)) {
      return "";
    }
    if (isDateValidFrontend(val)) {
      return formatDate(val);
    }
    try {
      if (val.startsWith("[")) {
        const valParsed = JSON.parse(val);
        if (valParsed.constructor === Array) {
          return (
            <Flex wrap="wrap" justify="flex-start">
              {valParsed.map((tag, index) => (
                <Badge key={`${tag}-${index}`} className="greybadge" size="sm" radius="4px" fz="11px">
                  {tag}
                </Badge>
              ))}
            </Flex>
          );
        }
      }
    } catch (error) {
      console.warn(error);
    }

    return val;
  };

  const hasUsername = useMemo(() => logs?.some((log) => log.userName != null) ?? false, [logs]);
  const hasUserDotName = useMemo(() => logs?.some((log) => log.user?.name != null) ?? false, [logs]);

  const columns: ColDef<IHistoryLogDepricated>[] = useMemo(
    () => [
      {
        field: "columnDisplayName",
        headerName: "Kolonne",
        filter: "agSetColumnFilter",
        width: 180,
        comparator: (a, b) => String(a || "").localeCompare(String(b || "")),
      },
      { field: "_id", hide: true },
      {
        field: "newValue",
        headerName: "Ny verdi",
        flex: 1,
        cellStyle: { padding: "6px 0px" },
        filter: "agTextColumnFilter",
        cellRenderer: (params) => formatValue(params.value),
      },
      {
        field: "oldValue",
        headerName: "Forrige verdi",
        flex: 1,
        cellStyle: { padding: "6px 0px" },
        filter: "agTextColumnFilter",
        cellRenderer: (params) => formatValue(params.value),
      },
      {
        field: "userName",
        headerName: "Redigert av",
        filter: "agTextColumnFilter",
        width: 160,
        hide: !hasUsername,
      },
      {
        field: "user.name",
        headerName: "Redigert av",
        filter: "agTextColumnFilter",
        width: 160,
        hide: !hasUserDotName,
      },
      {
        field: "updatedAt",
        headerName: "Endret dato",
        width: 132,
        sort: "desc",
        valueFormatter: (p) => (isDateValidFrontend(p.value) ? formatShortDate(p.value) : ""),
        comparator: (a, b) => {
          const da = isDateValidFrontend(a) ? new Date(a).getTime() : 0;
          const db = isDateValidFrontend(b) ? new Date(b).getTime() : 0;
          return da - db;
        },
      },
    ],
    [hasUsername, hasUserDotName],
  );

  return (
    <>
      {logs != null && (
        <div className="ag-theme-quartz" style={{ height: "100%", width: "100%" }}>
          <AgGridReact
            domLayout="autoHeight"
            rowData={logs}
            columnDefs={columns}
            defaultColDef={{
              suppressHeaderMenuButton: true,
              autoHeight: true,
              minWidth: 120,
              maxWidth: 500,
              cellStyle: {
                overflow: "hidden",
                whiteSpace: "normal",
                lineHeight: "1.5",
                wordBreak: "break-word",
              },
            }}
            rowHeight={30}
            headerHeight={35}
            suppressClickEdit
            animateRows={false}
            suppressContextMenu
            localeText={AG_GRID_LOCALE_NOR}
            getRowId={(p) => p.data._id}
          />
        </div>
      )}
    </>
  );
};

export default HistoryLogsDepricated;
